import './styles/index.css';
import './App.css';
import './styles/tailwind.css';
import './styles/notifications.css';
import './styles/christmas.css';
import './styles/enhanced.css';
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { signOut, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import awsconfig from './aws-exports';
import TabBar from './components/TabBar';
import Layout from './components/Layout/Layout';
import { NotificationProvider, useNotification, NOTIFICATION_TYPES } from './components/NotificationContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import { RefreshProvider } from './components/RefreshContext';
import NotificationToast from './components/NotificationToast';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { notificationService } from './services/notificationService';
import { useTranslationStore } from './services/translationService';
import { clearCache as clearUserTreeCache } from './services/userTreeService';
import { clearCache as clearUserSelfCache } from './services/userSelf';
import { clearCache as clearUsersDataCache } from './data/users';
import { isUserRestricted } from './services/breakglassService';

// Import pages
import Dashboard from './pages/Dashboard';
import AgentTree from './pages/AgentTree';
import Players from './pages/Players';
import Reports from './pages/Reports';
import BetHistory from './pages/BetHistory';
import AddTransfer from './pages/AddTransfer';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import SettingsModal from './components/SettingsModal';
import { SettingsModalProvider, useSettingsModal } from './components/SettingsModalContext';
import Transactions from './pages/Transactions';
import Login from './pages/Login';
import Users from './pages/Users';
import UserDeepView from './pages/UserDeepView';
import Logout from './pages/Logout';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminStatistics from './pages/AdminStatistics';
import Limits from './pages/Limits';
import Monitoring from './pages/Monitoring';
import ErrorBoundary from './components/ErrorBoundary';
// import TestTransactionBetFix from './pages/TestTransactionBetFix'; // DISABLED
import DetachInactivePlayers from './pages/DetachInactivePlayers';
import ModifyDomains from './pages/ModifyDomains';
import { ChristmasThemeProvider } from './components/ChristmasThemeContext';
import Snowflakes from './components/Snowflakes';
import AIAssistant from './components/AIAssistant';
import { EnhancedThemeProvider } from './components/EnhancedThemeContext';

// Configure Amplify
Amplify.configure(awsconfig);

// Restricted Route Guard - redirects restricted users to allowed pages only
const RestrictedRouteGuard = ({ children }) => {
  const [isRestricted, setIsRestricted] = React.useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const checkRestriction = async () => {
      const restricted = await isUserRestricted();
      setIsRestricted(restricted);
    };
    checkRestriction();
  }, []);

  React.useEffect(() => {
    if (isRestricted === true) {
      const allowedPaths = ['/reports', '/agent-tree', '/logout'];
      const currentPath = location.pathname;
      
      // If user is on an unauthorized page, redirect to reports
      if (!allowedPaths.some(path => currentPath.startsWith(path))) {
        console.log(`🔒 Restricted user tried to access ${currentPath}, redirecting to /reports`);
        navigate('/reports', { replace: true });
      }
    }
  }, [isRestricted, location.pathname, navigate]);

  // Show loading while checking restriction status (avoid a blank/white flash)
  if (isRestricted === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-500"></div>
      </div>
    );
  }

  return children;
};

// AuthenticatedApp component to handle authenticated routes and inactivity timeout
const AuthenticatedApp = ({ isAuthenticated, currentUser, onSignOut }) => {
  useInactivityTimeout();
  const { language, getDirection } = useTranslationStore();
  const direction = getDirection();
  const { isSettingsOpen, closeSettings } = useSettingsModal();
  const { showNotification } = useNotification();

  // Initialize inactivity warning listener
  useEffect(() => {
    const handleInactivityWarning = (event) => {
      const { timeLeft } = event.detail;
      showNotification(
        `Your session will expire in ${timeLeft} minutes due to inactivity. Please continue using the application to stay logged in.`,
        NOTIFICATION_TYPES.WARNING,
        10000 // Show warning for 10 seconds
      );
    };

    window.addEventListener('inactivityWarning', handleInactivityWarning);
    
    return () => {
      window.removeEventListener('inactivityWarning', handleInactivityWarning);
    };
  }, [showNotification]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <ErrorBoundary>
        <RestrictedRouteGuard>
          <div dir={direction} className={`flex flex-col min-h-screen ${direction === 'rtl' ? 'rtl' : ''}`}>
            <main className="flex-1 pb-[calc(4rem+env(safe-area-inset-bottom))] w-full max-w-7xl mx-auto px-2 sm:px-4">
              <Layout user={currentUser} onSignOut={onSignOut}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route 
                    path="/notifications" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Notifications />
                      </React.Suspense>
                    } 
                  />
                  <Route path="/transactions" element={<Transactions />} />
                  {/* Redirect /settings to dashboard since it's now a modal */}
                  <Route path="/settings" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/agent-tree" element={<AgentTree />} />
                  <Route path="/players" element={<Players />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/limits" element={<Limits />} />
                  <Route path="/monitoring" element={<Monitoring />} />
                  <Route path="/admin-analytics" element={<AdminAnalytics />} />
                  <Route path="/admin-statistics" element={<AdminStatistics />} />
                  <Route path="/bet-history" element={<BetHistory />} />
                  <Route path="/add-transfer" element={<AddTransfer />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/users/:userId/deep-view" element={<UserDeepView />} />
                  {/* DISABLED: <Route path="/test-transaction-bet-fix" element={<TestTransactionBetFix />} /> */}
                  <Route path="/detach-inactive-players" element={<DetachInactivePlayers />} />
                  <Route path="/modify-domains" element={<ModifyDomains />} />
                  <Route path="/logout" element={<Logout onSignOut={onSignOut} />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            </main>
            <TabBar />
          </div>
          
          {/* AI Panel Assistant — playwinadmin, after 30 profile-picture presses */}
          <AIAssistant />
          
          {/* Settings Modal */}
          <SettingsModal 
            isOpen={isSettingsOpen} 
            onClose={closeSettings}
            onSignOut={onSignOut}
          />
        </RestrictedRouteGuard>
      </ErrorBoundary>
    </>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isProcessingLogin, setIsProcessingLogin] = useState(false);
  const { language, getDirection, t } = useTranslationStore();
  const direction = getDirection();

  // Initialize theme from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) {
        const isDark = JSON.parse(saved);
        if (isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    } catch (error) {
      console.error('Error initializing theme from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    checkAuthState();
  }, []);

  // Global authentication error handler
  useEffect(() => {
    const handleAuthError = async (event) => {
      console.error('Global auth error handler triggered:', event.detail);
      
      // Prevent multiple simultaneous logout attempts
      if (!isAuthenticated) {
        return;
      }
      
      // Clear auth state immediately to prevent further API calls
      setIsAuthenticated(false);
      setCurrentUser(null);
      
      try {
        // Sign out from Amplify
        await signOut({ global: true });
        
        // Clear all service caches
        clearUserTreeCache();
        clearUserSelfCache();
        clearUsersDataCache();
        
        // Clean up notification service
        notificationService.cleanup();
        
        // Clear localStorage
        localStorage.removeItem('userPreferences');
        localStorage.removeItem('impersonatedUser');
        localStorage.removeItem('originalUser');
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        console.log('🔒 Session expired - user logged out');
      } catch (error) {
        console.error('Error during auto-logout:', error);
      } finally {
        // Force redirect to login with session expired message
        window.location.href = '/login?reason=session_expired';
      }
    };

    window.addEventListener('authError', handleAuthError);
    
    return () => {
      window.removeEventListener('authError', handleAuthError);
    };
  }, [isAuthenticated]);

  const checkAuthState = async () => {
    try {
      // First check if we have a current user
      const currentUser = await getCurrentUser();
      
      // Only fetch session if we have a current user
      if (currentUser) {
        const session = await fetchAuthSession();
        // Check if we have a valid session with tokens
        if (session?.tokens?.accessToken) {
          const groups = session.tokens.accessToken.payload['cognito:groups'] || [];
          // Allow access if user has any valid group (not just Admins)
          if (groups.length > 0 && !groups.includes('Disabled')) {
            setIsAuthenticated(true);
            setCurrentUser({
              username: session.tokens.accessToken.payload.username,
              groups: groups
            });
          } else {
            await signOut();
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } catch (error) {
      console.log('Authentication error:', error);
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = useCallback(async (username, session) => {
    if (isProcessingLogin) {
      console.log('Login already in progress, ignoring duplicate call');
      return;
    }

    setIsProcessingLogin(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));

      if (session?.tokens?.accessToken) {
        const groups = session.tokens.accessToken.payload['cognito:groups'] || [];
        setIsAuthenticated(true);
        setCurrentUser({
          username: username,
          groups: groups
        });
      }
    } catch (error) {
      console.error('Error setting up user session:', error);
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setTimeout(() => {
        setIsProcessingLogin(false);
      }, 1000);
    }
  }, [isProcessingLogin]);
  
  const handleSignOut = async () => {
    try {
      await signOut({ global: true });
      setIsAuthenticated(false);
      setCurrentUser(null);
      
      // Clear all service caches
      clearUserTreeCache();
      clearUserSelfCache();
      clearUsersDataCache();
      
      // Clean up notification service
      notificationService.cleanup();
      
      // Clear any other potential caches and user-specific data
      if (window.refreshUserTreeData) {
        delete window.refreshUserTreeData;
      }
      
      // Clear user-specific localStorage items
      localStorage.removeItem('userPreferences');
      localStorage.removeItem('impersonatedUser');
      localStorage.removeItem('originalUser');
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear any in-memory caches that might exist
      if (window.userTypeInitialized !== undefined) {
        window.userTypeInitialized = false;
      }
      
      console.log('🧹 All caches and user data cleared during logout');
      
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <EnhancedThemeProvider>
    <ChristmasThemeProvider>
    <NotificationProvider>
      <ConfirmProvider>
      <RefreshProvider>
        <SettingsModalProvider>
          <div dir={direction} className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <Snowflakes />
          <NotificationToast />
          <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
            isLoading ? '' : 'hidden'
          }`}>
            <div className="bg-white p-6 rounded-lg shadow-lg z-50 flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-500"></div>
              <p className="text-lg">{t('common.loading')}</p>
            </div>
          </div>
          
          <Router>
            {isLoading ? null : (
              <Routes>
                <Route 
                  path="/login" 
                  element={
                    isAuthenticated ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <Login onLogin={handleLogin} />
                    )
                  } 
                />
                <Route 
                  path="/*" 
                  element={
                    <AuthenticatedApp 
                      isAuthenticated={isAuthenticated}
                      currentUser={currentUser}
                      onSignOut={handleSignOut}
                    />
                  } 
                />
              </Routes>
            )}
          </Router>
        </div>
      </SettingsModalProvider>
      </RefreshProvider>
      </ConfirmProvider>
    </NotificationProvider>
    </ChristmasThemeProvider>
    </EnhancedThemeProvider>
  );
}

export default App;
